import selectingStaffOptions from '../../../shared_configs/bookingSelectingStaffOptions';
import Location from './steps/Location';
import Service from './steps/Service';
import Staff from './steps/Staff';
import DateTime from './steps/DateTime';
import Client from './steps/Client';
import Complete from './steps/Complete';

const initializeSteps = () => ({
  location: new Location(),
  service: new Service(),
  staff: new Staff(),
  datetime: new DateTime(),
  client: new Client(),
  complete: new Complete(),
});

class BaseFlow {
  constructor(options) {
    this.options = options;
    this.steps = initializeSteps();
  }

  initializeStepsData() {
    const { location, service, staff, datetime, client, complete } = this.steps;

    return {
      location: location.getDefaultData(),
      service: service.getDefaultData(),
      staff: staff.getDefaultData(),
      datetime: datetime.getDefaultData(),
      client: client.getDefaultData(),
      complete: complete.getDefaultData(),
    };
  }

  hideStaffStepByBookingSetting(stepsData) {
    const { bookingSettings } = this.options;

    if (stepsData.staff.isHidden) {
      return stepsData;
    }

    if (bookingSettings.selectingStaffOption === selectingStaffOptions.autoAssigned.option) {
      return {
        ...stepsData,
        staff: {
          ...stepsData.staff,
          isHidden: true,
        },
      };
    }

    return stepsData;
  };

  getFirstStep(stepsData) {
    const stepData = Object.values(stepsData)
      .sort((step1, step2) => step1.stepId - step2.stepId)
      .find(({ isHidden }) => !isHidden);
    return Object.values(this.steps).find(({ id }) => id === stepData.stepId);
  }

  getPrevStep(step, stepsData) {
    const stepData = Object.values(stepsData)
      .sort((step1, step2) => step2.stepId - step1.stepId)
      .find(({ stepId }) => stepId < step.id);
    return Object.values(this.steps).find(({ id }) => id === stepData.stepId);
  }

  getPrevVisibleStep(step, stepsData) {
    const stepData = Object.values(stepsData)
      .sort((step1, step2) => step2.stepId - step1.stepId)
      .find(({ stepId, isHidden }) => !isHidden && stepId < step.id);
    if (!stepData) {
      return;
    }

    return Object.values(this.steps).find(({ id }) => id === stepData.stepId);
  }

  getNextVisibleStep(step, stepsData) {
    const stepData = Object.values(stepsData)
      .sort((step1, step2) => step1.stepId - step2.stepId)
      .find(({ stepId, isHidden }) => !isHidden && stepId > step.id);
    return Object.values(this.steps).find(({ id }) => id === stepData.stepId);
  }

  updateStepsData(stepsData, newData) {
    return {
      ...stepsData,
      ...newData,
    };
  }

  resetDataForNextSteps(step, stepsData) {
    const steps = Object.values(this.steps);

    const resetData = Object.values(stepsData)
      .sort((step1, step2) => step1.stepId - step2.stepId)
      .filter(({ stepId, isHidden, allowToReset }) => (
        (!isHidden || (isHidden && allowToReset)) && stepId > step.id)
      )
      .reduce((acc, { stepId }) => {
        const step = steps.find(({ id }) => id === stepId);
        return {
          ...acc,
          ...step.resetOptions(stepsData),
        };
      }, {});

    return this.updateStepsData(stepsData, resetData);
  }

  resetStepData(step, stepsData) {
    const stepData = step.resetOptions(stepsData);

    return this.updateStepsData(stepsData, stepData);
  }

  getStepsResultData(stepsData) {
    const { location, service, staff, datetime } = this.steps;

    return {
      ...location.prepareResultData(stepsData),
      ...service.prepareResultData(stepsData),
      ...staff.prepareResultData(stepsData),
      ...datetime.prepareResultData(stepsData),
    };
  }
}

export default BaseFlow;
